import { useState } from 'react'
import { TypographyLabel, TypographyParagraph } from '../../typography'
import { UserProfile } from '@dynamic-labs/sdk-react-core'
import { CheckboxFilledIcon, CheckboxUnfilledIcon } from '../../icons'
import fallbackAvatarImage from '#/images/fallbackAvatar.webp'
import { ContentTile } from '../../content'
import { PillTab } from '../../tabs'
import UserAccountLookupInput from '../userAccountLookupInput'
import InputField from '../inputField'
import ImageURLInputField from '../imageUrlInputField'
import ArtistAvatarImageInputField from '../artistAvatarImageInput'

export interface UserType {
	id?: string | undefined
	username: string
	address?: string
	addressArray?: string[]
	avatar?: string
}

type userInputEnum = 'loreUser' | 'manual'

export default function ArtistAttributionSection({
	user,
	users,
	formData,
	setFormData,
	requiredFields,
	errorFields,
	selected,
	handleSetSelected,
	handleUnsetSelected,
	handleDataChange,
	handleImageChange,
}: {
	user: UserProfile
	users: UserType[]
	formData: any
	setFormData: (data: any) => void
	requiredFields: any[]
	errorFields: { [key: string]: { [key: string]: string } }
	selected: UserType | undefined
	handleSetSelected: (val: UserType) => void
	handleUnsetSelected: () => void
	handleDataChange: (name: string, value: any) => void
	handleImageChange: (val: string) => void
}) {
	const [userIsArtist, setUserIsArtist] = useState(
		selected && selected.username ? false : true,
	)
	const [artistInputMode, setArtistInputMode] = useState<userInputEnum>(
		selected && selected.id ? 'loreUser' : 'manual',
	)
	const userName = user && user.username ? user.username : ''
	const userAvatar =
		user && user.metadata && user.metadata?.avatar
			? user.metadata?.avatar
			: fallbackAvatarImage

	return (
		<div className="flex w-full flex-col gap-y-2">
			<TypographyLabel size="medium" isBold={true}>
				Artist Attribution
			</TypographyLabel>
			<TypographyParagraph
				size="small"
				additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
			>
				Credit the artist who created this collection.
			</TypographyParagraph>
			<TypographyParagraph
				size="small"
				additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
			>
				By default, you'll be credited as the artist, but you can change this if
				you're creating the collection on behalf of someone else.
			</TypographyParagraph>
			<div className="w-full cursor-pointer rounded-lg border border-textInactiveLight p-2 dark:border-textInactiveDark">
				<div
					className="flex w-full items-center gap-x-2 bg-uiTertiaryLight px-4 hover:bg-uiSecondaryLight dark:bg-uiTertiaryDark"
					onClick={() => {
						if (userIsArtist) {
							setUserIsArtist(false)
						} else {
							handleUnsetSelected()
							setUserIsArtist(true)
						}
					}}
				>
					{userIsArtist ? (
						<CheckboxFilledIcon size="small" />
					) : (
						<CheckboxUnfilledIcon size="small" />
					)}
					<ContentTile
						variant="user"
						image={userAvatar}
						title={userName}
						description="Credit me as the Artist"
						showBorder={false}
						showCaret={false}
					/>
				</div>
				{!userIsArtist && (
					<div className="mt-4 flex flex-col gap-y-4">
						<div className="flex gap-x-4">
							<PillTab
								title="Search Artist"
								size="medium"
								selected={artistInputMode === 'loreUser'}
								onClick={() => {
									handleUnsetSelected()
									setArtistInputMode('loreUser')
								}}
							/>
							<PillTab
								title="Add Manually"
								size="medium"
								selected={artistInputMode === 'manual'}
								onClick={() => {
									handleUnsetSelected()
									setArtistInputMode('manual')
								}}
							/>
						</div>
						{artistInputMode === 'loreUser' ? (
							<UserAccountLookupInput
								value={selected}
								setValue={handleSetSelected}
								handleUnsetSelected={handleUnsetSelected}
								placeholder={"Enter an Artist's name or address to search..."}
								users={users}
								isInactive={false}
								containerWidth="w-full"
							/>
						) : (
							<div className="flex w-full flex-col gap-y-2">
								<InputField
									name="artist name *"
									type="text"
									placeholder="Enter the artist's name"
									helper="This will be the artist's name in the collection page"
									containerWidth="w-full"
									value={formData.artistName}
									onChange={e => {
										handleDataChange('artistName', e.target.value)
									}}
									isError={requiredFields.find(
										requiredField => requiredField.slug == 'artistName',
									)}
								/>
								{/* <ImageURLInputField
									name={'artist picture *'}
									value={formData.artistAvatar}
									onChange={val => handleDataChange('artistAvatar', val)}
									isError={requiredFields.find(
										requiredField => requiredField.slug == 'artistAvatar',
									)}
									aspectRatio={1}
									uploadDescription={
										'Recommended size : 800x800. JPG, PNG, GIF'
									}
								/> */}
								<ArtistAvatarImageInputField
									name="Avatar *"
									uploadDescription="Recommended size: 800px x 800px. JPG, PNG, GIF"
									aspectRatio={1}
									value={
										selected && selected.avatar ? selected.avatar : undefined
									}
									onChange={v => handleImageChange(v)}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
